import './Merchant.css';
import { useMerchant } from './useMerchant';
import { useTranslation } from 'react-i18next';
import {
	UnprocessableError,
	validateImageFile,
	LocaleCode,
	FieldError,
	Image,
	LocalizedTexts,
	MerchantToSet,
	MerchantImageType,
	MERCHANT_LOGO_MIN_HEIGHT,
	MERCHANT_LOGO_MIN_WIDTH,
	MERCHANT_COVER_MIN_WIDTH,
	MERCHANT_COVER_MIN_HEIGHT,
	transformMerchantImage,
} from '@anticatrade/shared';
import {
	ImageBox,
	ImageEditor,
	Spinner,
	LocalizedField,
	DropZone,
	LocalizedFieldset,
	useDeferredLoading,
} from '@anticatrade/shared';
import SlInput from '@shoelace-style/shoelace/dist/react/input/index.js';
import SlButton from '@shoelace-style/shoelace/dist/react/button/index.js';
import SlSkeleton from '@shoelace-style/shoelace/dist/react/skeleton/index.js';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { AppContext, appContext } from '../../../context/AppContext';
import * as icons from '../../../constants/icons';
import { ErrorField } from '../../base/ErrorField/ErrorField';
import { LightBox } from '../../base/LightBox/LightBox';
import { Heading } from '../../base/Heading/Heading';
import { analytics } from '../../../analytics';

interface ImageToEdit {
	url: string;
	type: MerchantImageType;
	position: number | null;
}

const Merchant = () => {
	const [email, setEmail] = useState<string>();
	const [phoneNumber, setPhoneNumber] = useState<string>();
	const [landlineNumber, setLandlineNumber] = useState<string>();
	const [whatsappNumber, setWhatsappNumber] = useState<string>();
	const [website, setWebsite] = useState<string>();
	const [specialisation, setSpecialisation] = useState<LocalizedTexts | {}>({});
	const [history, setHistory] = useState<LocalizedTexts | {}>({});
	const [businessHours, setBusinessHours] = useState<LocalizedTexts | {}>({});
	const [logo, setLogo] = useState<Image>();
	const [cover, setCover] = useState<Image>();
	const [imageToEdit, setImageToEdit] = useState<ImageToEdit>();
	const [isEditorOpen, setIsEditorOpen] = useState<boolean>(false);
	const [fieldError, setFieldError] = useState<FieldError>();
	const [isLoadingImages, setIsLoadingImages] = useState<boolean>();
	const [isUploadingLogo, setIsUploadingLogo] = useState<boolean>(false);
	const [isUploadingCover, setIsUploadingCover] = useState<boolean>(false);
	const [lightboxIndex, setLightboxIndex] = useState<number>(0);
	const [isLightboxOpen, setIsLightboxOpen] = useState<boolean>(false);
	const [isSaving, setIsSaving] = useState<boolean>(false);

	const logoInputRef = useRef<any>();
	const coverInputRef = useRef<any>();
	const isFirstEffect = useRef<boolean>(true);

	const { handleError, showStatus, refreshMerchant, settings } = useContext(
		appContext as React.Context<AppContext>,
	);
	const { isLoading, merchant, saveMerchant } = useMerchant();
	const { t, i18n } = useTranslation();

	const { showSpinner, showContent } = useDeferredLoading(isLoading);

	const objectURLs = useRef<string[]>([]);

	const revokeObjectURLs = () => {
		for (const url of objectURLs.current) {
			URL.revokeObjectURL(url);
		}
		objectURLs.current = [];
	};

	useEffect(() => {
		return () => {
			revokeObjectURLs();
		};
	}, []);

	useEffect(() => {
		if (merchant == null) {
			return;
		}

		const setData = async () => {
			if (isFirstEffect.current) {
				isFirstEffect.current = false;
			} else {
				revokeObjectURLs();
			}

			setEmail(merchant.email);

			if (merchant.phoneNumber != null) {
				setPhoneNumber(merchant.phoneNumber);
			}
			if (merchant.landlineNumber != null) {
				setLandlineNumber(merchant.landlineNumber);
			}
			if (merchant.whatsappNumber != null) {
				setWhatsappNumber(merchant.whatsappNumber);
			}
			if (merchant.website != null) {
				setWebsite(merchant.website);
			}

			const specialisationTexts: LocalizedTexts = {} as LocalizedTexts;
			for (const lang in merchant.texts) {
				const l = lang as LocaleCode;
				const t = merchant.texts[l].specialisation;
				specialisationTexts[l] = t == null ? '' : t;
			}
			setSpecialisation(specialisationTexts);

			const historyTexts: LocalizedTexts = {} as LocalizedTexts;
			for (const lang in merchant.texts) {
				const l = lang as LocaleCode;
				const t = merchant.texts[l].history;
				historyTexts[l] = t == null ? '' : t;
			}
			setHistory(historyTexts);

			const businessHours: LocalizedTexts = {} as LocalizedTexts;
			for (const lang in merchant.texts) {
				const l = lang as LocaleCode;
				const t = merchant.texts[l].businessHours;
				businessHours[l] = t == null ? '' : t;
			}
			setBusinessHours(businessHours);

			setIsLoadingImages(true);

			const logoImg: Image = {
				url: `${import.meta.env.VITE_MERCHANT_IMAGE_URL}/${merchant.id}/${
					merchant.logo.fileName
				}.webp`,
				position: null,
			};
			setLogo(logoImg);

			if (merchant.cover != null && merchant.cover.fileName !== '') {
				const coverImg: Image = {
					url: `${import.meta.env.VITE_MERCHANT_IMAGE_URL}/${merchant.id}/${
						merchant.cover.fileName
					}.webp`,
					position: null,
				};
				setCover(coverImg);
			}

			setIsLoadingImages(false);
		};

		setData();
	}, [merchant]);

	useEffect(() => {
		if (imageToEdit !== undefined) {
			setIsEditorOpen(true);
		} else {
			setIsEditorOpen(false);
		}
	}, [imageToEdit]);

	const lightboxImages = useMemo(() => {
		const images = [];
		if (logo != null) {
			images.push({ src: logo.url, title: t('Logo') });
		}
		if (cover != null) {
			images.push({ src: cover.url, title: t('Cover image') });
		}
		return images;
	}, [logo, cover]);

	const onEmailInput = (e: any) => {
		const v = e.target.value;
		setEmail(v.trim() === '' ? undefined : v);
	};

	const onPhoneNumberInput = (e: any) => {
		const v = e.target.value;
		setPhoneNumber(v.trim() === '' ? undefined : v);
	};

	const onLandlineNumberInput = (e: any) => {
		const v = e.target.value;
		setLandlineNumber(v.trim() === '' ? undefined : v);
	};

	const onWhatsappNumberInput = (e: any) => {
		const v = e.target.value;
		setWhatsappNumber(v.trim() === '' ? undefined : v);
	};

	const onWebsiteInput = (e: any) => {
		const v = e.target.value;
		setWebsite(v.trim() === '' ? undefined : v);
	};

	const onSpecialisationInput = (text: string, language: LocaleCode) => {
		const s = { ...specialisation };
		s[language] = text.trim() === '' ? undefined : text;
		setSpecialisation(s);
	};

	const onHistoryInput = (text: string, language: LocaleCode) => {
		const h = { ...history };
		h[language] = text.trim() === '' ? undefined : text;
		setHistory(h);
	};

	const onBusinessHoursInput = (text: string, language: LocaleCode) => {
		const b = { ...businessHours };
		b[language] = text.trim() === '' ? undefined : text;
		setBusinessHours(b);
	};

	const onImageUpload = async (e: any, from: 'drop' | 'upload', type: MerchantImageType) => {
		setFieldError(undefined);

		let isUploadingSetter;
		if (type === 'logo') {
			isUploadingSetter = setIsUploadingLogo;
		} else {
			isUploadingSetter = setIsUploadingCover;
		}

		isUploadingSetter(true);

		let file: File | null;
		if (from === 'drop') {
			file = Array.from(e.dataTransfer.files)[0] as File;
		} else {
			file = Array.from(e.target.files)[0] as File;
		}
		if (file == null) {
			isUploadingSetter(false);
			return;
		}

		let minWidth: number;
		let minHeight: number;
		let field: string;
		if (type === 'logo') {
			minWidth = MERCHANT_LOGO_MIN_WIDTH;
			minHeight = MERCHANT_LOGO_MIN_HEIGHT;
			field = 'logo';
		} else {
			// It's the cover.
			minWidth = MERCHANT_COVER_MIN_WIDTH;
			minHeight = MERCHANT_COVER_MIN_HEIGHT;
			field = 'cover';
		}

		const locale = i18n.language as LocaleCode;

		try {
			await validateImageFile(file, minWidth, minHeight, false, true, locale);
		} catch (err) {
			isUploadingSetter(false);
			const er = err as Error;
			e.target.value = '';
			setTimeout(() => {
				setFieldError(new FieldError(er.message, field));
			}, 300);
			return;
		}

		let processed: File;
		try {
			processed = await transformMerchantImage(file, locale);
		} catch (err) {
			isUploadingSetter(false);
			const er = err as Error;
			e.target.value = '';
			setTimeout(() => {
				setFieldError(new FieldError(er.message, field));
			}, 300);
			return;
		}

		const fileURL = URL.createObjectURL(processed);
		objectURLs.current.push(fileURL);
		setImageToEdit({ url: fileURL!, type: type, position: null });
		isUploadingSetter(false);
	};

	const onProcessImage = async (image: any) => {
		const file = image.dest;

		let minWidth: number;
		let minHeight: number;
		if (imageToEdit!.type === 'logo') {
			minWidth = MERCHANT_LOGO_MIN_WIDTH;
			minHeight = MERCHANT_LOGO_MIN_HEIGHT;
		} else {
			// It's the cover.
			minWidth = MERCHANT_COVER_MIN_WIDTH;
			minHeight = MERCHANT_COVER_MIN_HEIGHT;
		}

		const locale = i18n.language as LocaleCode;

		try {
			await validateImageFile(image.dest, minWidth, minHeight, false, true, locale);
		} catch (err) {
			// Avoid saving the processed image as it violates
			// validation.
			const er = err as Error;
			handleError(`${t('The modified image could not be saved')}: ${er.message}`);
			setImageToEdit(undefined);
			return;
		}

		const fileURL = URL.createObjectURL(image.dest);
		objectURLs.current.push(fileURL);
		if (imageToEdit!.type === 'logo') {
			setLogo({ file: file, url: fileURL, position: null });
		} else {
			// It's the cover.
			setCover({ file: file, url: fileURL, position: null });
		}
		setImageToEdit(undefined);
		setIsEditorOpen(false);
	};

	const onAddImage = (type: MerchantImageType) => {
		if (type === 'logo') {
			logoInputRef.current.click();
		} else {
			// It's the cover.
			coverInputRef.current.click();
		}
	};

	const onEditImage = (type: MerchantImageType) => {
		if (type === 'logo') {
			setImageToEdit({
				url: logo!.url,
				type: 'logo',
				position: null,
			});
		} else {
			// It's the cover.
			setImageToEdit({
				url: cover!.url,
				type: 'cover',
				position: null,
			});
		}
	};

	const onRemoveImage = (type: MerchantImageType) => {
		if (type === 'logo') {
			logoInputRef.current.value = '';
			setLogo(undefined);
		} else {
			// It's the cover.
			coverInputRef.current.value = '';
			setCover(undefined);
		}
	};

	const onSeeFullscreenImage = (type: MerchantImageType) => {
		const hasLogo = logo != null;
		let index = 0;
		if (type === 'cover') {
			if (hasLogo) {
				index += 1;
			}
		}
		setLightboxIndex(index);
		setIsLightboxOpen(true);
	};

	const onSave = async () => {
		setFieldError(undefined);
		setIsSaving(true);

		const texts = {
			it: {
				specialisation: (specialisation as LocalizedTexts)['it'],
				history: (history as LocalizedTexts)['it'],
				businessHours: (businessHours as LocalizedTexts)['it'],
			},
			en: {
				specialisation: (specialisation as LocalizedTexts)['en'],
				history: (history as LocalizedTexts)['en'],
				businessHours: (businessHours as LocalizedTexts)['en'],
			},
			fr: {
				specialisation: (specialisation as LocalizedTexts)['fr'],
				history: (history as LocalizedTexts)['fr'],
				businessHours: (businessHours as LocalizedTexts)['fr'],
			},
		};

		const m = {
			email: email,
			phoneNumber: phoneNumber,
			landlineNumber: landlineNumber,
			whatsappNumber: whatsappNumber,
			website: website,
			texts: texts,
		} as MerchantToSet;

		try {
			await saveMerchant(m, logo, cover);
		} catch (err) {
			setIsSaving(false);
			if (err instanceof FieldError) {
				const field = document.querySelector(`#error__field-${err.field}`);
				if (field != null) {
					setTimeout(() => {
						setFieldError(err);
						field.scrollIntoView({ behavior: 'smooth', block: 'center' });
					}, 300);
				}
				return;
			} else if (err instanceof UnprocessableError && err.code === 'InvalidEmail') {
				handleError(t('Email is invalid'));
				return;
			}
			handleError(err as Error);
			return;
		}

		analytics.track('Profile Edited');
		setTimeout(() => {
			setIsSaving(false);
			refreshMerchant();
			showStatus({
				variant: 'success',
				icon: icons.OK,
				text: t('Profile data saved successfully'),
			});
		}, 300);
	};

	return (
		<div className='merchant'>
			{showContent ? (
				<div className='merchant__content'>
					<Heading
						left={<h1 className='merchant__title'>{t('Profile')}</h1>}
						right={
							merchant?.slug && settings != null ? (
								<a
									className='merchant__showcase-link'
									href={`${import.meta.env.VITE_SHOWCASE_URL}/${settings.languageCode}/merchants/${merchant.slug}`}
									target='_blank'
									rel='noopener noreferrer'
								>
									{t('View in the showcase')}
								</a>
							) : undefined
						}
					/>
					<div className='merchant__columns'>
						<div className='merchant__columns-left'>
							<div className='merchant__section merchant__section--info wrapped'>
								<div className='merchant__section-title'>{t('Information')}</div>
								<LocalizedFieldset
									initialLanguage={i18n.language as LocaleCode}
									t={t}
								>
									<ErrorField name='specialisation' error={fieldError}>
										<LocalizedField
											texts={specialisation as LocalizedTexts}
											label={t('Specialisation')}
											isRequired={true}
											onInputText={onSpecialisationInput}
											size='small'
											t={t}
											rows={3}
										/>
									</ErrorField>
									<ErrorField name='history' error={fieldError}>
										<LocalizedField
											texts={history as LocalizedTexts}
											label={t('History')}
											isRequired={true}
											onInputText={onHistoryInput}
											size='small'
											t={t}
											rows={8}
										/>
									</ErrorField>
									<ErrorField name='business-hours' error={fieldError}>
										<LocalizedField
											texts={businessHours as LocalizedTexts}
											label={t('Business hours')}
											isRequired={false}
											onInputText={onBusinessHoursInput}
											size='small'
											t={t}
											rows={5}
										/>
									</ErrorField>
								</LocalizedFieldset>
							</div>
							<div className='merchant__section merchant__section--images wrapped'>
								<div className='merchant__section-title'>{t('Images')}</div>
								<div className='merchant__image-editor-wrapper'>
									<ImageEditor
										src={imageToEdit?.url}
										imageCropAspectRatio={
											imageToEdit?.type === 'logo'
												? 1
												: imageToEdit?.type === 'cover'
													? 32 / 9
													: 16 / 9
										}
										onProcess={onProcessImage}
										isOpen={isEditorOpen}
										setIsOpen={setIsEditorOpen}
										willRenderCanvas={(shapes, state) => {
											if (imageToEdit?.type !== 'logo') {
												return { ...shapes };
											}
											// If it is the logo, show the
											// circle reticle to give a
											// preview of how the image
											// will be displayed.
											const {
												utilVisibility,
												selectionRect,
												lineColor,
												backgroundColor,
											} = state;
											if (utilVisibility.crop <= 0) return shapes;
											const { x, y, width, height } = selectionRect;
											return {
												...shapes,
												interfaceShapes: [
													{
														x: x + width * 0.5,
														y: y + height * 0.5,
														rx: width * 0.5,
														ry: height * 0.5,
														opacity: utilVisibility.crop,
														inverted: true,
														backgroundColor: [...backgroundColor, 0.5],
														strokeWidth: 1,
														strokeColor: [...lineColor],
													},
													...shapes.interfaceShapes,
												],
											};
										}}
										locale={i18n.language as LocaleCode}
										t={t}
									/>
								</div>
								<LightBox
									images={lightboxImages}
									isOpen={isLightboxOpen}
									index={lightboxIndex}
									closeFunc={() => setIsLightboxOpen(false)}
								/>
								<ErrorField
									name='logo'
									error={fieldError}
									className='merchant__image merchant__image--logo'
								>
									<div>
										<div className='merchant__image-title'>{t('Logo')}</div>
										{isLoadingImages || isUploadingLogo ? (
											<SlSkeleton
												className='merchant__image-skeleton merchant__image-skeleton--logo'
												effect='sheen'
											/>
										) : logo ? (
											<ImageBox
												url={logo.url}
												position={null}
												isCircle={true}
												onEditImage={
													logo.file != null
														? () => onEditImage('logo')
														: undefined
												}
												onRemoveImage={() => onRemoveImage('logo')}
												onSeeFullscreenImage={() =>
													onSeeFullscreenImage('logo')
												}
												t={t}
											/>
										) : (
											<div className='merchant__image-upload'>
												<DropZone
													multiple={false}
													isCircle={true}
													onDropFile={(e) =>
														onImageUpload(e, 'drop', 'logo')
													}
													onClick={() => onAddImage('logo')}
													t={t}
												/>
											</div>
										)}
										<input
											className='merchant__image-input'
											ref={logoInputRef}
											type='file'
											accept='image/jpeg, image/png, image/webp'
											onChange={(e) => onImageUpload(e, 'upload', 'logo')}
											multiple={false}
										/>
									</div>
								</ErrorField>
								<ErrorField
									name='cover'
									error={fieldError}
									className='merchant__image merchant__image--cover'
								>
									<div>
										<div className='merchant__image-title'>
											{t('Cover image')}
										</div>
										{isLoadingImages || isUploadingCover ? (
											<SlSkeleton
												className='merchant__image-skeleton merchant__image-skeleton--cover'
												effect='sheen'
											/>
										) : cover ? (
											<ImageBox
												url={cover.url}
												position={null}
												isCircle={false}
												onEditImage={
													cover.file != null
														? () => onEditImage('cover')
														: undefined
												}
												onRemoveImage={() => onRemoveImage('cover')}
												onSeeFullscreenImage={() =>
													onSeeFullscreenImage('cover')
												}
												t={t}
											/>
										) : (
											<div className='merchant__image-upload'>
												<DropZone
													multiple={false}
													onDropFile={(e) =>
														onImageUpload(e, 'drop', 'cover')
													}
													onClick={() => onAddImage('cover')}
													t={t}
												/>
											</div>
										)}
										<input
											className='merchant__image-input'
											ref={coverInputRef}
											type='file'
											accept='image/jpeg, image/png, image/webp'
											onChange={(e) => onImageUpload(e, 'upload', 'cover')}
											multiple={false}
										/>
									</div>
								</ErrorField>
							</div>
						</div>
						<div className='merchant__columns-right'>
							<div className='merchant__section merchant__section--contacts wrapped'>
								<div className='merchant__section-title'>
									{t('Contacts and website')}
								</div>
								<ErrorField name='email' error={fieldError}>
									<SlInput
										className='merchant__input'
										label={t('Email')}
										value={email == null ? '' : email}
										size='small'
										onSlInput={onEmailInput}
										required
									/>
								</ErrorField>
								<ErrorField name='phone-number' error={fieldError}>
									<SlInput
										className='merchant__input'
										label={t('Phone number')}
										value={phoneNumber == null ? '' : phoneNumber}
										size='small'
										onSlInput={onPhoneNumberInput}
									/>
								</ErrorField>
								<ErrorField name='landline-number' error={fieldError}>
									<SlInput
										className='merchant__input'
										label={t('Landline number')}
										value={landlineNumber == null ? '' : landlineNumber}
										size='small'
										onSlInput={onLandlineNumberInput}
									/>
								</ErrorField>
								<ErrorField name='whatsapp-number' error={fieldError}>
									<SlInput
										className='merchant__input'
										label={t('Whatsapp number')}
										value={whatsappNumber == null ? '' : whatsappNumber}
										size='small'
										onSlInput={onWhatsappNumberInput}
									/>
								</ErrorField>
								<ErrorField name='website' error={fieldError}>
									<SlInput
										className='merchant__input'
										label={t('Website')}
										value={website == null ? '' : website}
										size='small'
										onSlInput={onWebsiteInput}
									/>
								</ErrorField>
							</div>
						</div>
					</div>
					<div className='merchant__save'>
						<SlButton
							className='merchant__save-button'
							onClick={isLoadingImages ? undefined : onSave}
							variant='primary'
							disabled={isLoadingImages}
							loading={isSaving}
						>
							{t('Save')}
						</SlButton>
					</div>
				</div>
			) : (
				showSpinner && <Spinner />
			)}
		</div>
	);
};

export { Merchant };
